/** Organization acquisition is independent of a customer's optional friend/promo code. */
export const ACQUISITION_CODE_PARAM = "vsrc";
export const ACQUISITION_CLICK_PARAM = "vclid";
const STORAGE_KEY = "visaro_pending_acquisition";
const MAX_WINDOW_MS = 30 * 86_400_000;

export type PendingAcquisition = {
  code: string;
  clickId: string;
  expiresAt: number;
};

// Keeps navigation working when browser storage is unavailable. No fingerprinting or customer PII.
let memory: PendingAcquisition | null = null;

function valid(value: unknown): value is PendingAcquisition {
  if (!value || typeof value !== "object") return false;
  const v = value as PendingAcquisition;
  return typeof v.code === "string" && /^[A-Z0-9]{6,32}$/.test(v.code)
    && typeof v.clickId === "string"
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v.clickId)
    && Number.isFinite(v.expiresAt) && v.expiresAt > Date.now();
}

export function setPendingAcquisition(value: PendingAcquisition | null): void {
  memory = value;
  try {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(STORAGE_KEY);
    // Discard the previous, unbounded tab-only format.
    sessionStorage.removeItem("visaro_pending_acquisition_code");
    sessionStorage.removeItem("visaro_pending_acquisition_click");
  } catch {
    // Tracking must not break signup in restricted/private browsers.
  }
}

export function getPendingAcquisitionFromStorage(): PendingAcquisition | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const stored: unknown = raw ? JSON.parse(raw) : null;
    if (valid(stored)) return (memory = stored);
    memory = null;
  } catch {
    // Use the in-memory carrier when storage is unavailable.
  }
  if (valid(memory)) return memory;
  setPendingAcquisition(null);
  return null;
}

export function clearPendingAcquisition(): void {
  setPendingAcquisition(null);
}

/** Earliest unexpired visit in this browser wins. Core independently validates the stored click. */
export function captureAcquisitionFromParams(params: URLSearchParams): PendingAcquisition | null {
  const stored = getPendingAcquisitionFromStorage();
  if (stored) return stored;
  const next = {
    code: (params.get(ACQUISITION_CODE_PARAM) ?? "").trim().toUpperCase(),
    clickId: params.get(ACQUISITION_CLICK_PARAM) ?? "",
    // Only redirect-issued, bounded carriers qualify; a missing timestamp is not renewed.
    expiresAt: Math.min(Number(params.get("vexp")), Date.now() + MAX_WINDOW_MS),
  };
  if (!valid(next)) return null;
  setPendingAcquisition(next);
  return next;
}

export function acquisitionSignupFields(
  pending: PendingAcquisition | null,
): { acquisition_code?: string; acquisition_click_id?: string } {
  if (!valid(pending)) return {};
  return { acquisition_code: pending.code, acquisition_click_id: pending.clickId };
}
